/**
 * AB#21240 — labels for the two queue tabs on the orders dashboard.
 *
 * "Pending For Factory Assignment" and "Pending Merchant Approval" are accurate
 * but they cost most of the tab row, and both begin with the same word, so the
 * eye has to reach the fourth word to tell them apart. Shortening them to
 * "Factory" and "Approval" was worse: on this screen there is already a Factory
 * column, and approval happens at four different stages.
 *
 * These two queues are not the same kind of waiting. One is work sitting on
 * your desk — nobody has picked a factory yet. The other is work sitting on
 * someone else's — the merchant has not replied. Naming who is blocked answers
 * the question you open the tab to ask.
 *
 * The server's own name is kept as the tooltip, so anyone who learned the old
 * wording can confirm they are in the right place, and so a label change here
 * can never hide which queue the API actually returned.
 */

export interface TabLabel {
  /** What the tab shows. */
  label: string;
  /** What it says on hover — always the server's own name. */
  title: string;
}

const OVERRIDES: Record<string, string> = {
  pending_for_factory_assignment: 'Needs factory',
  pending_merchant_approval: 'With merchant',
};

/**
 * Resolve the label and tooltip for one tab.
 *
 * `displayName` is whatever the API sent. It is the fallback for both fields,
 * so a new tab, a renamed tab or a missing override all still render their real
 * name rather than an empty chip.
 */
export const tabLabel = (
  cleanedKey: string | null | undefined,
  displayName: string | null | undefined
): TabLabel => {
  const serverName = (displayName ?? '').trim();
  const override = cleanedKey ? OVERRIDES[cleanedKey] : undefined;

  if (!override) {
    return { label: serverName, title: serverName };
  }

  // A tooltip identical to the label is noise, so only keep it when it adds the
  // fuller name back.
  return { label: override, title: serverName || override };
};

/** The keys this module relabels. Exported so a test can assert the list. */
export const relabelledTabKeys = (): string[] => Object.keys(OVERRIDES);
